import axios from "axios";
import { isOnPublicRoute } from "@/lib/publicRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "abm_access_token";
// Local key used to persist autosave drafts when a request fails because the
// session expired. Pages (e.g. ClientDetail) can read this on remount and
// re-submit the recovered payload, so the operator never loses their work.
const PENDING_DRAFTS_KEY = "abm_pending_drafts";

// In-memory fallback for the access token. Necessary when the app runs
// inside a third-party iframe (e.g. the embedded Emergent preview):
// Safari ITP and Chrome Storage Partitioning silently reject `localStorage.
// setItem` in that context, so the bearer token would otherwise be lost
// the moment we try to persist it. The cookie-based session still works
// when the browser allows third-party cookies, but we can't rely on it
// alone — Safari blocks SameSite=None cookies in iframes by default.
//
// Keeping the token here means: as long as the iframe page is open, all
// subsequent requests carry the Authorization header and behave normally.
// On a page reload the user has to log in again — acceptable trade-off
// for an embedded preview.
let _memToken = null;

export function getToken() {
  if (_memToken) return _memToken;
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token) {
  _memToken = token || null;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Storage blocked (third-party iframe, private mode, full quota…).
    // The in-memory copy above is the fallback.
  }
}
export function clearToken() {
  _memToken = null;
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

// ─────────────────────────────────────────────────────────────────────────────
// Pending-drafts store — used when the session expires mid-autosave so the
// operator's unsaved data is preserved across the forced re-login.
// ─────────────────────────────────────────────────────────────────────────────
function readDrafts() {
  try {
    const raw = localStorage.getItem(PENDING_DRAFTS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeDrafts(map) {
  try { localStorage.setItem(PENDING_DRAFTS_KEY, JSON.stringify(map)); } catch {}
}
/** Persist a single failed write (autosave / form submit) for later recovery. */
export function persistDraft(key, payload) {
  if (!key) return;
  const all = readDrafts();
  all[key] = { payload, savedAt: new Date().toISOString() };
  writeDrafts(all);
}
/** Pop a previously persisted draft (returns null if none). */
export function popDraft(key) {
  if (!key) return null;
  const all = readDrafts();
  const item = all[key] || null;
  if (item) { delete all[key]; writeDrafts(all); }
  return item;
}
/** Read without removing — useful to know we have something to recover. */
export function peekDraft(key) {
  return readDrafts()[key] || null;
}

const api = axios.create({
  baseURL: API,
  withCredentials: true, // primary: httpOnly cookies
});

// Fallback: attach Bearer token from localStorage for browsers that block cookies
// (Safari ITP, strict privacy, incognito mode, cross-site iframes, etc.)
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// 401 → silent refresh + retry. Single-flight so N concurrent 401s only
// trigger ONE call to /auth/refresh; the others queue and replay after.
// ─────────────────────────────────────────────────────────────────────────────
let refreshPromise = null;

function isAuthRefreshUrl(url) {
  return typeof url === "string" && /\/auth\/refresh(\?|$)/.test(url);
}
function isAuthLoginUrl(url) {
  // Login / register failing with 401 is an authentication error (wrong
  // credentials), not an expired session — never try to "refresh" out of
  // those. `/auth/me` IS retryable: it's a guarded read-only endpoint and
  // a 401 there during normal navigation means the access cookie expired
  // while the refresh cookie is still valid → silent refresh + retry is
  // exactly the desired behaviour.
  return typeof url === "string" && /\/auth\/(login|register)(\?|$)/.test(url);
}
function isAuthMeUrl(url) {
  // `/auth/me` is the boot probe AuthContext fires on EVERY page mount,
  // including public pages (the AuthProvider wraps BrowserRouter). For
  // anonymous visitors a 401 there is expected behaviour — they have no
  // session, AuthContext will simply flip `user=false`. We must NOT treat
  // it as a "session expired" and redirect, otherwise opening any public
  // share link (/request-quote/, /cliente/, /trial/, …) bounces the
  // visitor straight to /login.
  return typeof url === "string" && /\/auth\/me(\?|$)/.test(url);
}

async function performRefresh() {
  // Use a fresh axios instance so this call doesn't itself pass through the
  // 401 interceptor (which would loop infinitely on a refresh failure).
  const r = await axios.post(`${API}/auth/refresh`, {}, {
    withCredentials: true,
    // Allow Bearer-fallback clients to send the refresh token via header if
    // they have one. Most clients have nothing here and rely on the cookie.
    headers: {},
  });
  // Backend echoes the new access_token in JSON for Bearer-fallback clients.
  const newAccess = r?.data?.access_token;
  if (newAccess) setToken(newAccess);
  return true;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const cfg = err?.config || {};
    const url = cfg.url || "";

    // 402 (plan_limit) — broadcast to BillingProvider for the upgrade modal.
    if (status === 402 && detail && typeof detail === "object" && detail.code === "plan_limit") {
      window.dispatchEvent(new CustomEvent("plan-limit-hit", { detail }));
      return Promise.reject(err);
    }

    // Only intercept 401 — and only ONCE per request (cfg._retried) to avoid
    // infinite loops when refresh succeeds but the original request still
    // returns 401 (e.g. user got revoked). Refresh and login endpoints are
    // explicitly skipped: refreshing in response to a refresh-401 would loop;
    // a login-401 means the password was wrong, surface it untouched.
    //
    // Two extra short-circuits to never disturb anonymous visitors on
    // public pages:
    //   • `/auth/me` 401 from a visitor without a stored token → expected,
    //     just let AuthContext set user=false. No refresh, no redirect.
    //   • Any 401 fired from a public route (e.g. /request-quote/:slug)
    //     should never redirect — the visitor isn't supposed to be logged
    //     in there. We still attempt the refresh in case the SAME tab had
    //     a valid session (e.g. operator previewing their own lead link),
    //     but we suppress the redirect on failure.
    const onPublicRoute = isOnPublicRoute(window.location.pathname);
    const tokenStored = !!getToken();
    if (
      status !== 401 ||
      cfg._retried ||
      isAuthRefreshUrl(url) ||
      isAuthLoginUrl(url) ||
      (isAuthMeUrl(url) && !tokenStored)
    ) {
      return Promise.reject(err);
    }

    cfg._retried = true;

    try {
      // Single-flight: every concurrent 401 awaits the same refreshPromise.
      if (!refreshPromise) {
        refreshPromise = performRefresh().finally(() => {
          // Clear the slot AFTER the promise settles so any 401 that arrives
          // *while* the refresh is in flight piggybacks on this same call.
          refreshPromise = null;
        });
      }
      await refreshPromise;
    } catch (refreshErr) {
      // Refresh failed → session is dead. Persist any in-flight body as a
      // recovery draft (keyed by URL+method so the page can find it on
      // remount), then redirect to /login. We never throw a popup here —
      // the page will surface a single sober toast on next mount.
      try {
        if (cfg.method && cfg.method !== "get" && cfg.data) {
          let body = cfg.data;
          if (typeof body === "string") {
            try { body = JSON.parse(body); } catch { /* keep string */ }
          }
          persistDraft(`${cfg.method.toUpperCase()} ${url}`, body);
        }
      } catch { /* localStorage may be full / disabled — ignore */ }

      clearToken();
      // Notify any AuthProvider so it can flip its state instantly without
      // waiting for the redirect.
      window.dispatchEvent(new CustomEvent("auth-session-expired"));

      // Public pages (landing, /login, /cliente/, /trial/, /request-quote/,
      // /timeline/share/, /privacy, …) must NEVER trigger the
      // "session expired" redirect. The visitor isn't supposed to have a
      // session there, and being bounced mid-page-load destroys the share
      // link UX (the user reported this on /request-quote/:slug).
      // We also skip when the failing call was `/auth/me` — that's the
      // boot probe AuthContext fires on every mount; a 401 there is an
      // expected anonymous response, AuthContext already handles it.
      if (!onPublicRoute && !isAuthMeUrl(url)) {
        const path = window.location.pathname;
        const next = encodeURIComponent(path + window.location.search);
        // Use replace so back-button doesn't return to the failed page.
        window.location.replace(`/login?next=${next}&session=expired`);
      }
      return Promise.reject(err);
    }

    // Refresh succeeded → replay the original request EXACTLY as it was
    // (headers, body, params, withCredentials all preserved by axios). The
    // request interceptor will re-attach the (refreshed) Bearer fallback.
    return api(cfg);
  }
);

export default api;

export function formatApiError(detail) {
  if (detail == null) return "Si è verificato un errore. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return String(detail);
}
